import {ErrorHandler, OnCompleteCallback} from "../../../utils";
import {FC, useState} from "react";
import {
  OrganizationAddress,
  toOrganizationAddress,
  UpdateAddressInput
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {ModalWithAlert, useAlert} from "../../../alerting";
import {useTranslation} from "react-i18next";
import {useOperationalEntity} from "../../context";
import {
  LookupOperationalEntity,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  UpdateOperationalEntityAddress,
  UpdateOperationalEntityAddressData,
  UpdateOperationalEntityAddressVariables,
  assertOperationalEntityPresent
} from "../../backend";
import {Endpoints} from "../../../runtime";
import {useGraphqlLazyQuery, useGraphqlMutation} from "../../../amplify/hooks";
import {
  lookupOperationalEntity as lookupOperationalEntityQuery,
  updateOperationalEntityAddress as updateAddressMutation
} from "../../../amplify/graphql";
import {RadioButtonList} from "@interstate/components/RadioButtonList";
import "./EditAddressModal.scss";
import {
  PrimaryClientAddress,
  PrimaryAddressMap,
  matchingPrimaryAddress,
  toRadioButtonOption
} from "./addresses";

export const EditAddressModal: FC<{
  id: string;
  currentAddress: OrganizationAddress;
  primaryAddresses: PrimaryClientAddress[];
  onComplete: OnCompleteCallback;
}> = ({id, currentAddress, onComplete, primaryAddresses}) => {
  const {t} = useTranslation();
  const {alert, clearAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });
  const [working, setWorking] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    () => primaryAddresses.find(matchingPrimaryAddress(currentAddress))?.key
  );
  const [primaryAddressMap] = useState<PrimaryAddressMap>(() =>
    primaryAddresses.reduce((accumulator, primary) => {
      accumulator[primary.key] = primary;
      return accumulator;
    }, {} as PrimaryAddressMap)
  );
  const {entity, setEntity} = useOperationalEntity();

  const [lookUpOperationalEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({
    document: LookupOperationalEntity,
    amplifyQuery: lookupOperationalEntityQuery,
    context: {endpoint: Endpoints.APPSYNC},
    fetchPolicy: "network-only"
  });

  const [updateAddress] = useGraphqlMutation<
    UpdateOperationalEntityAddressData,
    UpdateOperationalEntityAddressVariables
  >({
    document: UpdateOperationalEntityAddress,
    amplifyMutation: updateAddressMutation,
    context: {endpoint: Endpoints.APPSYNC}
  });

  const clearErrors = (): void => {
    clearAlert();
  };

  const handleError: ErrorHandler = (error: any) => {
    createErrorAlert(error);
  };

  const refreshEntity = async (): Promise<void> => {
    return await lookUpOperationalEntity({variables: {id: entity.id}})
      .then(assertOperationalEntityPresent)
      .then(setEntity);
  };

  const saveChanges = async () => {
    setWorking(true);
    clearErrors();
    const addressInput: UpdateAddressInput = {
      address: toOrganizationAddress(primaryAddressMap[selectedValue!].address),
      entityId: id
    };
    await updateAddress({variables: {addressInput}})
      .then(result => {
        if (result.data?.entity.success === false) {
          throw new Error(t("common-errors.backend-error"));
        }
      })
      .then(refreshEntity)
      // If the update call returns an error, give the user the opportunity to try again
      .then(() => onComplete(true))
      .catch(handleError)
      .finally(() => setWorking(false));
  };

  const cancelChanges = () => {
    onComplete(false);
  };

  return (
    <ModalWithAlert
      id={"edit-address-modal"}
      data-testid={"edit-address-modal"}
      show={true}
      size={"large"}
      header={t("modal.header.edit-address")}
      alert={alert}
      footer={{
        primary: {
          action: saveChanges,
          label: t("common-actions.save-changes"),
          isLoading: working,
          disabled: !selectedValue
        },
        options: {
          action: cancelChanges,
          label: t("common-actions.cancel"),
          isLoading: working,
          buttonStyle: "tertiary"
        }
      }}
      onHide={cancelChanges}>
      <RadioButtonList
        id={"address-radio-list"}
        data-testid={"address-radio-list"}
        label={t("modal.text-input.edit-address")}
        name={"edit-address-list"}
        value={selectedValue}
        options={primaryAddresses.map(toRadioButtonOption)}
        onChange={event => setSelectedValue(event.target.value)}
      />
    </ModalWithAlert>
  );
};
